import {
  Button as RacButton,
  FieldError,
  Group,
  Input,
  Label,
  Text,
  SearchField,
  TextField as TextFieldBase,
  type TextFieldProps as TextFieldBaseProps,
  type SearchFieldProps,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { css, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { formField, type FormFieldVariant } from "../../../../styled-system/recipes";
import { FaMagnifyingGlass, FaPencil, FaX } from "react-icons/fa6";
import { useState } from "react";
import BulkEditModal from "../../core/basic/BulkEditModal";

interface SearchInputProps extends Omit<SearchFieldProps, "className"> {
  label?: string;
  errorMessage?: string;
  description?: string;
  placeholder?: string;
  css?: SystemStyleObject;
  isExpandable?: boolean;
  variant?: FormFieldVariant["variant"];
  startIcon?: React.ReactNode;
}

export const SearchInput = ({
  value,
  onChange,
  label,
  errorMessage,
  description,
  onSubmit,
  onClear,
  css: cssProp,
  isExpandable,
  variant,
  startIcon = <FaMagnifyingGlass className={css({ fontSize: "medium" })} strokeWidth={"15"} />,
  ...rest
}: SearchInputProps) => {
  const { t } = useTranslation(["common"]);
  const classes = formField({ variant });
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <SearchField
      className={cx(classes.fieldWrapper, css(cssProp))}
      value={value}
      onChange={onChange}
      onClear={onClear}
      onSubmit={onSubmit}
      isInvalid={!!errorMessage}
      {...rest}
    >
      {({ isEmpty, state }) => (
        <>
          {label && <Label className={classes.label}>{label}</Label>}
          <Group className={classes.inputBox}>
            {startIcon}
            <Input
              className={cx(
                classes.input,
                css({
                  "&::-webkit-search-cancel-button,&::-webkit-search-decoration": {
                    WebkitAppearance: "none",
                  },
                }),
              )}
            />

            {!isEmpty && (
              <RacButton
                className={css({
                  all: "unset",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: "small",
                  "&[data-focus-visible='true']": {
                    color: "white",
                    backgroundColor: "#aaa",
                  },
                })}
              >
                <FaX strokeWidth={"20"} />
              </RacButton>
            )}
            {isExpandable && (
              <RacButton
                slot={null}
                className={css({
                  all: "unset",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: "small",
                  "&[data-focus-visible='true']": {
                    color: "white",
                    backgroundColor: "#aaa",
                  },
                })}
                onPress={() => setIsExpanded(!isExpanded)}
              >
                <FaPencil strokeWidth={"20"} />
              </RacButton>
            )}
          </Group>
          {description && <Text slot="description">{description}</Text>}
          {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
          {isExpanded && (
            <BulkEditModal
              isOpen={isExpanded}
              onClose={() => setIsExpanded(false)}
              title={t(($) => $.common.actions.Search)}
              onSave={(values) => state.setValue(values.join(","))}
              defaultValues={state.value.split(",")}
            />
          )}
        </>
      )}
    </SearchField>
  );
};

interface TextFieldProps extends Omit<TextFieldBaseProps, "className"> {
  label?: React.ReactNode;
  description?: string;
  errorMessage?: string;
  css?: SystemStyleObject;
  variant?: FormFieldVariant["variant"];
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  placeholder?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  variant,
  startIcon,
  endIcon,
  placeholder,
  css: cssProp,
  ...props
}: TextFieldProps) {
  const classes = formField({ variant });
  return (
    <TextFieldBase isInvalid={!!errorMessage} {...props} className={cx(classes.fieldWrapper, css(cssProp))}>
      {label && <Label className={classes.label}>{label}</Label>}
      <Group className={classes.inputBox}>
        {startIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {startIcon}
          </span>
        )}
        <Input className={classes.input} placeholder={placeholder} />
        {endIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {endIcon}
          </span>
        )}
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
    </TextFieldBase>
  );
}
